/**
 * MKS-393 — `/hr/merch-sizes`: who has told us their sizes, who has not, and
 * how many of each to order.
 *
 * Two filters with deliberately different homes:
 *
 *   - `missingOnly` is a SERVER filter, because the XLSX and CSV endpoints
 *     take the same parameter — so a download always matches the table it was
 *     taken from;
 *   - the name/email search is CLIENT-side only, so typing in it cannot
 *     silently change what an export contains.
 *
 * Reachable by admin and hr (`HRRoute`).
 */
import { useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { MerchSizeChartsCard } from '../../components/hr/MerchSizeChartsCard';
import { useMerchExport, useMerchOverview } from '../../hooks/useMerchSizes';
import {
  MERCH_FIELD_LABELS,
  MERCH_SIZE_FIELDS,
  formatShoeSize,
  type MerchSizeField,
} from '../../constants/merchSizes';
import type { MerchOverviewRow } from '../../services/api/merchSizes';

/** The cell text for one merch field — the same rendering the exports use. */
function cellValue(row: MerchOverviewRow, field: MerchSizeField): string {
  const value = row[field];
  if (value === null) return '—';
  return field === 'merchShoeSize' ? formatShoeSize(value as number) : String(value);
}

export default function MerchSizesPage() {
  const [missingOnly, setMissingOnly] = useState(false);
  const [search, setSearch] = useState('');
  const { data, isLoading, isError } = useMerchOverview(missingOnly);
  const exportMutation = useMerchExport();

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return data?.rows ?? [];
    return (data?.rows ?? []).filter(
      (row) =>
        row.name.toLowerCase().includes(needle) || row.email.toLowerCase().includes(needle),
    );
  }, [data?.rows, search]);

  const incomplete = (data?.rows ?? []).filter((row) => row.missing.length > 0).length;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Merch sizes</h1>
          <p className="text-sm text-gray-500 mt-1">
            T-shirt, hoodie, sock and shoe sizes for active internal people. Everyone fills
            their own in on their profile.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => exportMutation.mutate({ format: 'xlsx', missingOnly })}
            disabled={exportMutation.isPending}
            className="flex items-center gap-2"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            XLSX
          </Button>
          <Button
            variant="secondary"
            onClick={() => exportMutation.mutate({ format: 'csv', missingOnly })}
            disabled={exportMutation.isPending}
            className="flex items-center gap-2"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            CSV
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="search"
            aria-label="Search by name or email"
            placeholder="Search by name or email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={missingOnly}
            onChange={(event) => setMissingOnly(event.target.checked)}
            className="rounded border-gray-300"
          />
          Only people with something missing
        </label>
        {data && (
          <span className="text-sm text-gray-500">
            {incomplete} of {data.totalPeople} incomplete
          </span>
        )}
      </div>

      {/*
        MKS-393 size charts — outside the table's loading/error branch on
        purpose: uploading a chart has nothing to do with whether the overview
        query succeeded, so a failed overview must not take the upload UI with
        it.
      */}
      <MerchSizeChartsCard />

      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Couldn&rsquo;t load the merch sizes. Try again in a moment.
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  {MERCH_SIZE_FIELDS.map((field) => (
                    <th key={field} className="px-4 py-3">
                      {MERCH_FIELD_LABELS[field]}
                    </th>
                  ))}
                  <th className="px-4 py-3">Missing</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-6 text-center text-gray-500">
                      Nobody matches that.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.userId}>
                      <td className="px-4 py-3 font-medium text-gray-900">{row.name}</td>
                      <td className="px-4 py-3 text-gray-600">{row.email}</td>
                      {MERCH_SIZE_FIELDS.map((field) => (
                        <td key={field} className="px-4 py-3 text-gray-900">
                          {cellValue(row, field)}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-gray-500">
                        {row.missing.length === 0
                          ? '—'
                          : row.missing.map((field) => MERCH_FIELD_LABELS[field]).join(', ')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <TableCellsIcon className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-semibold text-gray-900">How many of each</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {MERCH_SIZE_FIELDS.map((field) => (
                <div key={field}>
                  <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                    {MERCH_FIELD_LABELS[field]}
                  </h3>
                  {data && data.counts[field].length > 0 ? (
                    <ul className="space-y-1">
                      {data.counts[field].map((entry) => (
                        <li key={entry.value} className="flex justify-between text-sm">
                          <span className="text-gray-700">{entry.value}</span>
                          <span className="font-medium text-gray-900">{entry.count}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-400">Nobody has set this yet.</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
